import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CheckoutComponentMetric, ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { ExtensionGroupsService } from '@pages/checkout-app/checkout-layout/main/components/extension-groups/extension-groups.service';
import { IExtensionGroup } from '@pages/checkout-app/checkout-layout/main/components/extension-groups/models/extension-group.models';
import { CHECKOUT_URL } from '@pages/checkout-app/utils';
import { CheckoutSettings } from '@pdqdev/pdq-shared/dist/entities';
import { CheckoutTestGroups } from '@pdqdev/pdq-shared/dist/interfaces';
import { ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { of } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';
import { FormService } from '../../services';
import { ShippingMethods, Shippings } from '../../types';
import { CheckoutActions } from '../actions';

@Injectable()
export class CheckoutEffects {
  loadShippingMethods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadShippingMethods),
      switchMap(() => {
        return this.formService.loadShippingMethods().pipe(
          map((shippingMethods: ShippingMethods[]) => {
            return CheckoutActions.loadShippingsMethodsSuccess({
              shippingMethods,
            });
          }),
          catchError(() => {
            return of(CheckoutActions.loadShippingsMethodsFailure());
          }),
        );
      }),
    ),
  );
  loadCheckouts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadCheckouts),
      switchMap(() => {
        return this.formService.loadCheckouts().pipe(
          map((shippings: Shippings[]) => {
            return CheckoutActions.loadCheckoutsSuccess({ shippings });
          }),
          catchError((error) => {
            console.log('effect load failure');
            return of(
              CheckoutActions.loadCheckoutsFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );
  addNewShippingsCheckout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.addNewShippingsCheckout),
      switchMap(({ shippings }) => {
        return this.formService.addNewShipping(shippings).pipe(
          map((res) => {
            shippings = res;
            this.router.navigate([`${CHECKOUT_URL}`]);
            return CheckoutActions.addNewShippingsCheckoutSuccess({ shippings });
          }),
          catchError((error) => {
            this.router.navigate([`${CHECKOUT_URL}`]);
            return of(
              CheckoutActions.addNewShippingsCheckoutFailure({
                errorMessage: error.error?.message ?? error.error,
              }),
            );
          }),
        );
      }),
    ),
  );
  editCheckout = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.editCheckout),
      switchMap(({ shippings, shippingsId }) => {
        return this.formService.editCheckout(shippings, shippingsId).pipe(
          map((saved) => CheckoutActions.editCheckoutSuccess({ shippings, updatedAt: saved?.updatedAt })),
          catchError((error) => {
            return of(
              CheckoutActions.editCheckoutFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );
  loadCheckoutById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadCheckoutById),
      switchMap(({ checkoutId }) => {
        return this.formService.loadCheckoutById(checkoutId).pipe(
          map((shipping) => {
            console.log(shipping);
            return CheckoutActions.loadCheckoutByIdSuccess({ shipping });
          }),
          catchError(() => {
            return of(CheckoutActions.loadCheckoutByIdFailure());
          }),
        );
      }),
    ),
  );
  deleteCheckout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.deleteCheckout),
      switchMap(({ checkoutId }) => {
        return this.formService.onDeleteCheckout(checkoutId).pipe(
          map(() => {
            return CheckoutActions.deleteCheckoutSuccess({ checkoutId });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.deleteCheckoutFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  saveCheckoutSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.saveCheckoutSettings),
      switchMap(({ settings }) => {
        return this.formService.saveCheckoutSettings(settings).pipe(
          map((settings: CheckoutSettings) => {
            return CheckoutActions.saveCheckoutSettingsSuccess({ settings });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.saveCheckoutSettingsFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  editCheckoutSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.editCheckoutSettings),
      switchMap(({ settings }) => {
        return this.formService.editCheckoutSettings(settings).pipe(
          map((settings: CheckoutSettings) => {
            return CheckoutActions.editCheckoutSettingsSuccess({ settings });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.editCheckoutSettingsFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  loadCheckoutSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadCheckoutSettings),
      switchMap(() => {
        return this.formService.loadCheckoutSettings().pipe(
          map((settings) => {
            return CheckoutActions.loadCheckoutSettingsSuccess({ settings });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.loadCheckoutSettingsFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  loadPdqIqBaseShippingMethods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadPdqIqBaseShippingMethods),
      switchMap(({ shopId }) => {
        return this.formService.loadPdqIqBaseShippingMethods(shopId).pipe(
          map((shippingMethods) => {
            return CheckoutActions.loadPdqIqBaseShippingMethodsSuccess({ shippingMethods });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.loadPdqIqBaseShippingMethodsFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  loadVariantTestGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadVariantTestGroups),
      switchMap(({ originalVariantId }) => {
        return this.formService.loadVariantTestGroups(originalVariantId).pipe(
          map((variantTestGroups: CheckoutTestGroups) => {
            variantTestGroups = {
              original: variantTestGroups.original.map((testData) => ({
                ...testData,
                conversionsOfVisits: `${testData.conversions} / ${testData.visits}`,
              })),
              testGroups: variantTestGroups.testGroups
                ?.map((testGroup) => ({
                  data: testGroup?.data,
                  groups: testGroup?.groups.map((testData) => ({
                    ...testData,
                    conversionsOfVisits: `${testData.conversions} / ${testData.visits}`,
                  })),
                }))
                ?.reverse(),
            };
            return CheckoutActions.loadVariantTestGroupsSuccess({ variantTestGroups });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.loadVariantTestGroupsFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  saveVariantTest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.saveVariantTest),
      switchMap(({ variant }) => {
        return this.formService.saveVariantTest(variant).pipe(
          map((variant: any) => {
            return CheckoutActions.saveVariantTestSuccess({ variant });
          }),
          catchError((error) => {
            console.log(error);
            return of(
              CheckoutActions.saveVariantTestFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  saveControlAndVariantTest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.editRunningABTest),
      switchMap(({ payload }) => {
        return this.formService.editRunningABTest(payload).pipe(
          map((response: any) => {
            return CheckoutActions.editRunningABTestSuccess(response);
          }),
          catchError((error) => {
            console.log(error);
            return of(
              CheckoutActions.editRunningABTestFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  stopVariantTest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.stopVariantTest),
      switchMap(({ variantTestId }) => {
        return this.formService.stopVariantTest(variantTestId).pipe(
          map((variant: any) => {
            return CheckoutActions.stopVariantTestSuccess({ variant });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.stopVariantTestFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  deleteVariantTest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.deleteVariantTest),
      switchMap(({ variantTestId }) => {
        return this.formService.deleteVariantTest(variantTestId).pipe(
          map(() => {
            return CheckoutActions.deleteVariantTestSuccess({ variantTestId });
          }),
          catchError((error) => {
            console.log(error);
            return of(
              CheckoutActions.deleteVariantTestFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  setVariantTestWinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.setVariantTestWinner),
      switchMap(({ variantTestId, variantType }) => {
        return this.formService.setWinnerVariantTest(variantTestId, variantType).pipe(
          map((variant: any) => {
            return CheckoutActions.setVariantTestWinnerSuccess({ variant });
          }),
          catchError((error) => {
            console.log(error);
            return of(
              CheckoutActions.setVariantTestWinnerFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  editVariantTest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.editVariantTest),
      switchMap(({ variant }) => {
        return this.formService.updateVariantTest(variant).pipe(
          map((variant: any) => {
            return CheckoutActions.editVariantTestSuccess({ variant });
          }),
          catchError((error) => {
            console.log(error);
            return of(
              CheckoutActions.editVariantTestFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  loadExtensionsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadExtensionsList),
      switchMap(() => {
        return this.extensionsService.loadExtensions().pipe(
          map((extensions: ICheckoutExtensions[]) => {
            return CheckoutActions.loadExtensionsListSuccess({ extensions });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.loadExtensionsListFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  loadExtensionsListLightweight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadExtensionsListLightweight),
      switchMap(() => {
        return this.extensionsService.loadExtensionsLightweight().pipe(
          map((extensions: ICheckoutExtensions[]) => {
            return CheckoutActions.loadExtensionsListLightweightSuccess({ extensions });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.loadExtensionsListLightweightFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  loadNotLiveExtensionIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadExtensionsListLightweightSuccess, CheckoutActions.loadExtensionsListSuccess),
      map(({ extensions }) => extensions.filter((extension) => extension.enabled).map((extension) => extension.id)),
      switchMap((enabledExtensionIds) => {
        if (!enabledExtensionIds.length) {
          return of(CheckoutActions.loadNotLiveExtensionIdsSuccess({ notLiveExtensionIds: [] }));
        }
        return this.extensionsService.loadNotLiveExtensionIds(enabledExtensionIds).pipe(
          map((notLiveExtensionIds) => CheckoutActions.loadNotLiveExtensionIdsSuccess({ notLiveExtensionIds })),
          catchError(() => of(CheckoutActions.loadNotLiveExtensionIdsSuccess({ notLiveExtensionIds: [] }))),
        );
      }),
    ),
  );

  loadExtensionById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadExtensionById),
      switchMap(({ shopId, metricsFromDate, metricsToDate, extensionId, extensionType }) => {
        if (metricsFromDate && metricsToDate) {
          return this.extensionsService
            .loadMetrics(shopId, metricsFromDate, metricsToDate, extensionId, extensionType)
            .pipe(
              timeout(1500), // To prevent the user waiting too much to edit the extension, if the data is big, then it will be loaded from the edit screen
              map((metrics) => {
                return { extensionId, metrics };
              }),
              catchError((error) => {
                console.log(error);
                return of({ extensionId, metrics: [] });
              }),
            );
        } else {
          return of({ extensionId, metrics: [] });
        }
      }),
      switchMap(({ extensionId, metrics }) => {
        return this.extensionsService.loadExtensionById(extensionId).pipe(
          map((extension: ICheckoutExtensions) => {
            (extension as ICheckoutExtensions & { metrics: CheckoutComponentMetric[] }).metrics = metrics;

            return CheckoutActions.loadExtensionByIdSuccess({
              extension: extension as ICheckoutExtensions & { metrics: CheckoutComponentMetric[] },
            });
          }),
          catchError((error) => {
            console.log(error);
            return of(CheckoutActions.loadExtensionByIdFailure());
          }),
        );
      }),
    ),
  );
  saveExtension$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.saveExtension),
      switchMap(({ extension }) => {
        return this.extensionsService.createExtension(extension).pipe(
          map((extension: any) => {
            return CheckoutActions.saveExtensionSuccess({ extension });
          }),
          catchError((error) => {
            console.log(error);
            return of(
              CheckoutActions.saveExtensionFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  updateExtension$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.updateExtension),
      switchMap(({ id, extension }) => {
        return this.extensionsService.updateExtension(id, extension).pipe(
          map((extension: any) => {
            return CheckoutActions.updateExtensionSuccess({ extension });
          }),
          catchError((error) => {
            console.log(error);
            return of(
              CheckoutActions.updateExtensionFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  deleteExtension$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.deleteExtension),
      switchMap(({ extensionId }) => {
        return this.extensionsService.deleteExtension(extensionId).pipe(
          map(() => {
            return CheckoutActions.deleteExtensionSuccess({ extensionId });
          }),
          catchError((error) => {
            console.log(error);
            return of(
              CheckoutActions.deleteExtensionFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  duplicateExtension$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.duplicateExtension),
      switchMap(({ extensionId }) => {
        return this.extensionsService.loadExtensionById(extensionId).pipe(
          map((extension: ICheckoutExtensions) => {
            delete extension.id;
            delete extension.createdAt;
            delete extension.updatedAt;
            delete extension.usageToken;

            extension.template.title = `Copy of [${extension.template.title}]`;
            extension.template.internalName = `Copy of [${extension.template.internalName}]`;

            return CheckoutActions.duplicateExtensionSuccess({ extension });
          }),
          catchError((error) => {
            console.log(error);
            return of(CheckoutActions.duplicateExtensionFailure({ errorMessage: error.error }));
          }),
        );
      }),
    ),
  );

  // Extension Groups Effects
  loadExtensionGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadExtensionGroups),
      switchMap(() => {
        return this.extensionGroupsService.loadGroups().pipe(
          map((groups: IExtensionGroup[]) => {
            return CheckoutActions.loadExtensionGroupsSuccess({ groups });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.loadExtensionGroupsFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  loadExtensionGroupById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadExtensionGroupById),
      switchMap(({ groupId }) => {
        return this.extensionGroupsService.loadGroupById(groupId).pipe(
          map((group: IExtensionGroup) => {
            return CheckoutActions.loadExtensionGroupByIdSuccess({ group });
          }),
          catchError((error) => {
            return of(CheckoutActions.loadExtensionGroupByIdFailure());
          }),
        );
      }),
    ),
  );

  createExtensionGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.createExtensionGroup),
      switchMap(({ group }) => {
        return this.extensionGroupsService.createGroup(group).pipe(
          map((createdGroup: IExtensionGroup) => {
            return CheckoutActions.createExtensionGroupSuccess({ group: createdGroup });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.createExtensionGroupFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  updateExtensionGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.updateExtensionGroup),
      switchMap(({ id, group }) => {
        return this.extensionGroupsService.updateGroup(id, group).pipe(
          map((updatedGroup: IExtensionGroup) => {
            return CheckoutActions.updateExtensionGroupSuccess({ group: updatedGroup });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.updateExtensionGroupFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  deleteExtensionGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.deleteExtensionGroup),
      switchMap(({ groupId }) => {
        return this.extensionGroupsService.deleteGroup(groupId).pipe(
          map(() => {
            return CheckoutActions.deleteExtensionGroupSuccess({ groupId });
          }),
          catchError((error) => {
            return of(
              CheckoutActions.deleteExtensionGroupFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private router: Router,
    private formService: FormService,
    private extensionsService: ExtensionsService,
    private extensionGroupsService: ExtensionGroupsService,
    private dialog: MatDialog,
  ) {}
}
